import { useCallback, useEffect, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { BaseAutocomplete } from '../BaseAutocomplete/BaseAutocomplete';
import { FormAutocompleteSelectProps, GMSelectOption } from './FormAutocompleteSelect.types';
import { textCapitalize } from '../../../helpers/textCapitalize';
import { IServiceAdvisor } from '../../../API/GoMoto';
import { useScrollToOnError } from '../../../helpers/useScrollTo';

const FormAutocompleteSelect = <T,>({
    name,
    id,
    title,
    label,
    ariaLabel,
    labelClass,
    disabled,
    fullOptions,
    forceBottomPopper,
    capsLock = false,
    fullWidth = false,
    seventyFivePercentWidth = false,
}: FormAutocompleteSelectProps<T>) => {
    const [field, meta] = useField<string>(name);
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * This manages the internal state since we don't want to allow the MUI Autocomplete to
     * consider itself controlled. Instead we want MUI to consider itself uncontrolled
     * so that we can manually manage the internal state.
     */
    const [state, setState] = useState<GMSelectOption | null>(null);
    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    /**
     * Takes in the name of the service advisor selected and returns the name and id as an object to update
     * the internal state. Then we update formik with the new service advisor id selected.
     */
    const getOption = useCallback((value: string | null): void => {
        if (!value) {
            setState(null);
            setFieldValue(name, '');
            return;
        }

        /**
         * Finds the full service advisor object based on the name. Shifts the input name and the options names to locale lower case for a direct comparison.
         */
        const result: IServiceAdvisor | undefined = fullOptions.find((fullOption: IServiceAdvisor) => fullOption.name?.toLocaleLowerCase() === value.toLocaleLowerCase());

        if (result) {
            /**
             * Ensures that the name is properly capitalized for display.
             */
            setState({
                id: result.id,
                name: textCapitalize(result.name) ?? '',
            });
            setFieldValue(name, result.id);
            return;
        }
        else {
            setState(null);
            setFieldValue(name, '');
            return;
        }
    }, [fullOptions, name, setFieldValue]);

    useEffect(() => {
        /**
         * Handles updating the internal state when a formik value is present and not the same value as the internal state value is.
         */
        if ((!state && field.value !== '') || (state && field.value !== state.id)) {
            const name: string | undefined = fullOptions.find((fullOption: IServiceAdvisor) => fullOption.id?.toLocaleLowerCase() === field.value.toLocaleLowerCase())?.name;

            getOption(name ?? '');
        }
    }, [field.value, fullOptions, getOption, state]);

    /**
     * Filters the initial object to a string of names for displaying the menu options.
     */
    const advisorList: string[] = fullOptions ? fullOptions.map((fullOption: IServiceAdvisor) => textCapitalize(fullOption.name) ?? '') : [];

    return (
        <BaseAutocomplete
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            labelClass={labelClass}
            inputRef={inputRef}
            value={state?.name || null}
            inputValue={state?.name ?? ''}
            disabled={disabled}
            touched={meta.touched || submitCount > 0}
            error={meta.error}
            options={advisorList}
            onOptionSelected={(value: string | null) => getOption(value)}
            forceBottomPopper={forceBottomPopper}
            capsLock={capsLock}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            readOnly={true}
            selectOnFocus={false}
        />
    );
};

export default FormAutocompleteSelect;